import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Modal, Select } from 'antd';
import { getUserDetails, makeGetCall } from '../../../utils/helper';

export type CenterOption = { value: string; label: string };

/**
 * The current user's centers, as Select options. Built from the comma-separated
 * codes in user_unit_name, resolved to friendly labels via /center-options.
 * An empty array means the user has no center restriction (sees all centers).
 */
export const useUserCenters = (): CenterOption[] => {
  const [centers, setCenters] = useState<CenterOption[]>([]);

  useEffect(() => {
    const codes = String(getUserDetails()?.user_unit_name || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    if (codes.length === 0) {
      setCenters([]);
      return;
    }

    let cancelled = false;
    makeGetCall('/center-options')
      .then(res => {
        if (cancelled) return;
        const all: CenterOption[] = res?.data?.data || [];
        const mine = all.filter(o => codes.includes(o.value));
        setCenters(mine.length ? mine : codes.map(c => ({ value: c, label: c })));
      })
      .catch(() => {
        if (!cancelled) setCenters(codes.map(c => ({ value: c, label: c })));
      });

    return () => { cancelled = true; };
  }, []);

  return centers;
};

/**
 * Resolves which center a new ROOT public folder should belong to:
 *  - 0 centers (unrestricted user): resolves null (backend keeps it global)
 *  - 1 center: resolves it without prompting
 *  - >1 centers: prompts via modal; rejects if the user cancels (so the caller
 *    can abort the create)
 *
 * Returns { pickCenter, modal } — render {modal} somewhere in the tree.
 */
export const useCenterPicker = (centers: CenterOption[]) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string | undefined>(undefined);
  const resolveRef = useRef<((v: string | null) => void) | null>(null);
  const rejectRef = useRef<(() => void) | null>(null);

  const pickCenter = useCallback((): Promise<string | null> => {
    if (centers.length === 0) return Promise.resolve(null);
    if (centers.length === 1) return Promise.resolve(centers[0].value);
    setValue(centers[0].value);
    setOpen(true);
    return new Promise<string | null>((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
    });
  }, [centers]);

  const handleOk = () => {
    setOpen(false);
    resolveRef.current?.(value ?? null);
    resolveRef.current = null;
    rejectRef.current = null;
  };

  const handleCancel = () => {
    setOpen(false);
    rejectRef.current?.();
    resolveRef.current = null;
    rejectRef.current = null;
  };

  const modal = (
    <Modal
      title="Choose a center for this public folder"
      open={open}
      okText="Create"
      onOk={handleOk}
      onCancel={handleCancel}
      destroyOnClose
    >
      <p style={{ marginBottom: 8, color: 'var(--pacs-muted-fg, #64748B)' }}>
        This public folder will be visible only to users of the selected center.
      </p>
      <Select
        style={{ width: '100%' }}
        options={centers}
        value={value}
        onChange={setValue}
        placeholder="Select center"
      />
    </Modal>
  );

  return { pickCenter, modal };
};
